import React, { useState, useEffect, useCallback } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setPageTitle } from 'store/actions/header';

import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { BasicAudienceInfo } from './components/basic-info';
import { useAudienceService } from 'hooks/users/audience';
import { AudienceUpdationForm } from '../edit';
import { AudiencePageStatBar } from './components/stat-bar';
import { Spacer } from 'app/layouts/generic';
import { AudienceContacts } from './components/lists/contact';
import SlidingNav from 'app/shared/nav/sliding/sliding';
import useScreenSize from 'hooks/size';
import { AudienceQueryBuilder } from './components/query-builder';
import { AudienceCampaign } from './components/lists/campaign/campaigns';
import { useContactService } from 'hooks/users/contact';
import { getRelativeTimeStampRange } from 'app/shared/utils/date';
import { useCampaignService } from 'hooks/mailing/campaign';
import GmModal from 'app/shared/modal/modal';
import ImportContact from '../../contacts/import/import';
import { Button } from 'app/shared/button';
import { useSubscriptionService } from 'hooks/sales/subscription';

export const MailingListPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();
  const { fetchAudienceById } = useAudienceService();
  const { fetchCampaigns } = useCampaignService();
  const { fetchAudienceContacts } = useContactService();
  const { fetchSubscription } = useSubscriptionService();

  const [audience, setAudience] = useState({});
  const [audience_stats, setAudienceStats] = useState(false);
  const [contact_stat, setContactStat] = useState([]);
  const [can_use_query_builder, setCanUseQueryBuilder] = useState(false);
  const [is_loading_audience, setIsLoadingAudience] = useState(true);
  const [mode, setMode] = useState('view');
  const [show_imports_modal, setShowImportsModal] = useState(false);

  useEffect(() => {
    setIsLoadingAudience(() => true);
    fetchAudienceById(id)
      .then(({ audience }) => {
        setAudience(() => audience);
        dispatch(
          setPageTitle([
            { title: 'Audiences', path: '/audiences' },
            { title: audience.name || 'N/A', path: `/audiences/${id}` }
          ])
        );
      })
      .finally(() => setIsLoadingAudience(() => false));

    fetchSubscription({
      query_string: `plan_code=campaign&status=active`
    }).then(({ subscriptions: [subscription] }) => {
      setCanUseQueryBuilder(() => subscription && subscription.tier !== 'explorer');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const { this_month, last_month } = getRelativeTimeStampRange();
    Promise.all([
      fetchAudienceContacts(id, { query_string: `count=1&bool=is_valid` }),
      fetchAudienceContacts(id, {
        query_string: `time_stamp=${this_month.start}~${this_month.end}&count=1&bool=is_valid`
      }),
      fetchAudienceContacts(id, {
        query_string: `time_stamp=${last_month.start}~${last_month.end}&count=1&bool=is_valid`
      }),
      fetchAudienceContacts(id, { query_string: `count=1&bool=-is_valid` }),
      fetchAudienceContacts(id, {
        query_string: `time_stamp=${this_month.start}~${this_month.end}&count=1&bool=-is_valid`
      }),
      fetchAudienceContacts(id, {
        query_string: `time_stamp=${last_month.start}~${last_month.end}&count=1&bool=-is_valid`
      }),
      fetchCampaigns({ query_string: `mailing_lists=${id}&count=1` }),
      fetchCampaigns({
        query_string: `time_stamp=${this_month.start}~${this_month.end}&mailing_lists=${id}&count=1`
      }),
      fetchCampaigns({
        query_string: `time_stamp=${last_month.start}~${last_month.end}&mailing_lists=${id}&count=1`
      })
    ]).then((responses) => {
      const [
        { size: valid_all_time },
        { size: valid_this_month },
        { size: valid_last_month },
        { size: invalid_contacts },
        { size: invalid_contacts_this_month },
        { size: invalid_contacts_last_month },
        { size: campaign_all_time },
        { size: campaign_this_month },
        { size: campaign_last_month }
      ] = responses;

      setContactStat(() => [
        {
          label: 'Valid contacts',
          main_value: valid_all_time,
          current_value: valid_this_month,
          previous_value: valid_last_month
        },
        {
          label: 'Invalid contacts',
          main_value: invalid_contacts,
          current_value: invalid_contacts_this_month,
          previous_value: invalid_contacts_last_month
        },
        {
          label: 'Campaigns',
          main_value: campaign_all_time,
          current_value: campaign_this_month,
          previous_value: campaign_last_month
        }
      ]);
    });
  }, [audience_stats, id]);

  const handleUpdation = (data = null) => {
    if (!data) return;
    setAudience((curr_data) => ({ ...curr_data, ...data }));
    setMode(() => 'view');
  };

  const handleQueryChange = useCallback(() => {
    setAudienceStats((prev) => !prev);
  }, []);

  const show_query_builder =
    !is_loading_audience && !audience.is_test_audience && can_use_query_builder;

  const nav_config = [
    {
      title: 'Query Builder',
      path: `/audiences/${id}/query`,
      hidden: isMobile || !show_query_builder
    },
    { title: 'Valid Contacts', path: `/audiences/${id}/contacts/valid` },
    { title: 'Invalid Contacts', path: `/audiences/${id}/contacts/invalid` },
    { title: 'Campaigns', path: `/audiences/${id}/campaigns` }
  ].filter((item) => !item.hidden);

  return (
    <div className="dt-content-wrapper">
      <PageContentWrapper>
        <GridRow>
          {!isMobile && (
            <GridColumn>
              <GridRow num_of_columns={1}>
                <GridColumn>
                  {mode === 'view' ? (
                    <BasicAudienceInfo
                      audience_data={audience}
                      onEditClick={() => setMode('edit')}
                    />
                  ) : (
                    <AudienceUpdationForm audience_data={audience} onSave={handleUpdation} />
                  )}
                </GridColumn>
              </GridRow>
            </GridColumn>
          )}
          <GridColumn span={!isMobile ? 3 : 4}>
            {isMobile && <SlidingNav nav_items={nav_config} />}
            {!isMobile && <AudiencePageStatBar contact_stat={contact_stat} />}
            <Spacer multiple={4} />
            {!isMobile && (
              <SlidingNav
                nav_items={nav_config}
                actions={
                  <Button
                    text="Import contacts"
                    icon_name="upload"
                    disabled={
                      audience?.type === 'system' ||
                      !audience ||
                      !audience.id ||
                      audience.is_test_audience
                    }
                    onClick={() => setShowImportsModal(true)}
                  />
                }
              />
            )}
            <Routes>
              {show_query_builder && (
                <Route
                  path="query"
                  element={<AudienceQueryBuilder onQueryChange={handleQueryChange} />}
                />
              )}
              <Route path="contacts/valid" element={<AudienceContacts key="valid" />} />
              <Route path="campaigns" element={<AudienceCampaign />} />
              <Route
                path="contacts/invalid"
                element={<AudienceContacts key="invalid" is_valid={false} />}
              />
              <Route
                path="/*"
                element={
                  !isMobile ? (
                    show_query_builder ? (
                      <AudienceQueryBuilder onQueryChange={handleQueryChange} />
                    ) : (
                      <AudienceContacts key="valid" />
                    )
                  ) : mode === 'view' ? (
                    <BasicAudienceInfo
                      audience_data={audience}
                      onEditClick={() => setMode('edit')}
                    />
                  ) : (
                    <AudienceUpdationForm audience_data={audience} onSave={handleUpdation} />
                  )
                }
              />
            </Routes>
          </GridColumn>
        </GridRow>
        <GmModal
          title="Import contacts"
          show_title
          show_modal={show_imports_modal}
          onClose={() => setShowImportsModal(false)}
        >
          <ImportContact mailing_list={audience} />
        </GmModal>
      </PageContentWrapper>
    </div>
  );
};
